import {
  defaultObjectSizing,
  getShaderColorFromString,
  getShaderNoiseTexture,
  grainGradientFragmentShader,
  grainGradientMeta,
  GrainGradientShapes,
  ShaderFitOptions,
  ShaderMount,
  type GrainGradientShape,
  type GrainGradientUniforms,
  type ShaderMountUniforms
} from "@paper-design/shaders";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import useMediaQuery from "../../../shared/hooks/useMediaQuery";
import { REDUCED_MOTION_QUERY } from "../../../shared/hooks/usePrefersReducedMotion";
import "./kuika-grain-gradient.scss";

export interface KuikaGrainGradientProps {
  /** 1–7 stops (grainGradientMeta.maxColorCount is 7). Hex, rgb() or hsl() strings. */
  colors: readonly string[];
  /** Fully transparent by default so the host element's own background shows through. */
  colorBack?: string;
  shape?: GrainGradientShape;
  /** Colour transition sharpness: 0 hard edge, 1 smooth gradient. */
  softness?: number;
  /** Distortion between the colour bands. */
  intensity?: number;
  /** Grain overlay strength. Forced to 0 when the randomizer texture is unavailable. */
  noise?: number;
  /** 0 freezes the animation and stops the render loop entirely. */
  speed?: number;
  /**
   * Seeds the shader clock so a given composition is reproducible. Read once, at mount:
   * changing it later would rewind an animation that is already running.
   */
  frame?: number;
  scale?: number;
  /** Caps the physical pixels rendered per frame. Worth setting on large decorative surfaces. */
  maxPixelCount?: number;
  className?: string;
}

interface GrainGradientSettings {
  colors: readonly string[];
  colorBack: string;
  shape: GrainGradientShape;
  softness: number;
  intensity: number;
  noise: number;
  scale: number;
}

const CONTEXT_LOST_CLASS: string = "kuika-grain-gradient--context-lost";

/**
 * The shader declares `vec4 u_colors[7]`. Fewer than one stop trips the mount's
 * "Unsupported uniform array length: 0" warning and more than seven overruns the array,
 * so the list is clamped and the caller is told in development.
 */
const clampColors = (colors: readonly string[]): readonly string[] => {
  const maxCount: number = grainGradientMeta.maxColorCount;

  if (colors.length >= 1 && colors.length <= maxCount) {
    return colors;
  }

  if (process.env.NODE_ENV === "development") {
    console.warn(`KuikaGrainGradient: expected 1-${maxCount} colors, received ${colors.length}.`);
  }

  return colors.length === 0 ? ["#000000"] : colors.slice(0, maxCount);
};

/**
 * `getShaderNoiseTexture` builds a brand new `Image` from an inlined data URI on every
 * call, so calling it per render would churn uniform identity and miss the mount's
 * internal texture cache. One image for the whole app is enough.
 */
let cachedNoiseTexture: HTMLImageElement | undefined;
let noiseTextureRequested: boolean = false;

const getCachedNoiseTexture = (): HTMLImageElement | undefined => {
  if (!noiseTextureRequested) {
    noiseTextureRequested = true;
    cachedNoiseTexture = getShaderNoiseTexture();
  }

  return cachedNoiseTexture;
};

const isTextureReady = (image: HTMLImageElement | undefined): boolean => {
  return image !== undefined && image.complete && image.naturalWidth > 0;
};

/**
 * `ShaderMount` throws on an image that has not finished decoding, so it has to be
 * awaited before the mount is constructed. `decode` is missing in jsdom, which is the
 * signal to give up on the texture rather than to wait forever.
 */
const decodeNoiseTexture = async (image: HTMLImageElement | undefined): Promise<HTMLImageElement | undefined> => {
  if (!image) {
    return undefined;
  }

  if (isTextureReady(image)) {
    return image;
  }

  if (typeof image.decode !== "function") {
    return undefined;
  }

  try {
    await image.decode();
  } catch {
    return undefined;
  }

  return isTextureReady(image) ? image : undefined;
};

const buildUniforms = (
  settings: GrainGradientSettings,
  noiseTexture: HTMLImageElement | undefined
): ShaderMountUniforms => {
  // Inferred rather than annotated, then checked with `satisfies`: `ShaderMountUniforms`
  // is an index-signature interface, and only an inferred object type picks up the
  // implicit index signature that makes it assignable. Annotating the local as
  // `GrainGradientUniforms` (an interface) would not.
  const uniforms = {
    u_colorBack: getShaderColorFromString(settings.colorBack),
    u_colors: settings.colors.map((color: string): [number, number, number, number] => getShaderColorFromString(color)),
    u_colorsCount: settings.colors.length,
    u_softness: settings.softness,
    u_intensity: settings.intensity,
    // Without the randomizer texture the grain term would sample an unbound sampler,
    // so it is switched off rather than left at the requested strength.
    u_noise: noiseTexture ? settings.noise : 0,
    u_shape: GrainGradientShapes[settings.shape],
    u_fit: ShaderFitOptions[defaultObjectSizing.fit],
    u_scale: settings.scale,
    u_rotation: defaultObjectSizing.rotation,
    u_originX: defaultObjectSizing.originX,
    u_originY: defaultObjectSizing.originY,
    u_offsetX: defaultObjectSizing.offsetX,
    u_offsetY: defaultObjectSizing.offsetY,
    u_worldWidth: defaultObjectSizing.worldWidth,
    u_worldHeight: defaultObjectSizing.worldHeight,
    // An undefined uniform is skipped by the mount rather than bound as an empty
    // sampler, which is exactly what we want when the texture never decoded.
    u_noiseTexture: noiseTexture
  } satisfies GrainGradientUniforms;

  return uniforms;
};

/**
 * Animated multi-colour grain gradient, rendered with WebGL2.
 *
 * The element is decorative and childless on purpose: `ShaderMount` imperatively
 * prepends its canvas, which would fight React's reconciliation if this node also
 * owned children. Content belongs in a sibling.
 *
 * Two things the host element must provide:
 * - a background of its own, because the shader is silently skipped wherever WebGL2
 *   is unavailable (hardware acceleration off, virtual desktops, GPU blocklists) and
 *   nothing else will paint;
 * - no `filter`, `opacity` below 1, `transform`, `will-change` or `isolation`, since
 *   the canvas sits at `z-index: -1` inside this element's stacking context and any
 *   of those on an ancestor changes what it composites against.
 */
const KuikaGrainGradient: React.FC<KuikaGrainGradientProps> = ({
  colors,
  colorBack = "#00000000",
  shape = "corners",
  softness = 0.5,
  intensity = 0.5,
  noise = 0.25,
  speed = 1,
  frame = 0,
  scale = 1,
  maxPixelCount,
  className
}: KuikaGrainGradientProps) => {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const shaderRef = useRef<ShaderMount | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const prefersReducedMotion: boolean = useMediaQuery(REDUCED_MOTION_QUERY);
  const effectiveSpeed: number = prefersReducedMotion ? 0 : speed;

  const settings: GrainGradientSettings = {
    colors: clampColors(colors),
    colorBack,
    shape,
    softness,
    intensity,
    noise,
    scale
  };

  // The init effect runs once but must construct the mount with whatever the current
  // props are, so the latest values are mirrored into refs rather than closed over.
  const settingsRef = useRef<GrainGradientSettings>(settings);
  settingsRef.current = settings;
  const speedRef = useRef<number>(effectiveSpeed);
  speedRef.current = effectiveSpeed;
  const maxPixelCountRef = useRef<number | undefined>(maxPixelCount);
  maxPixelCountRef.current = maxPixelCount;
  const frameRef = useRef<number>(frame);
  frameRef.current = frame;

  useEffect((): (() => void) => {
    let cancelled: boolean = false;
    const element: HTMLDivElement | null = mountRef.current;

    const handleContextLost = (): void => {
      element?.classList.add(CONTEXT_LOST_CLASS);
      // Hiding the canvas is not enough on its own: the mount re-arms
      // requestAnimationFrame on every frame while its speed is non-zero, so without
      // this the page keeps paying for frames nobody can see.
      shaderRef.current?.setSpeed(0);
    };

    const start = async (): Promise<void> => {
      if (!element) {
        return;
      }

      const noiseTexture: HTMLImageElement | undefined = await decodeNoiseTexture(getCachedNoiseTexture());

      if (cancelled) {
        return;
      }

      try {
        shaderRef.current = new ShaderMount(
          element,
          grainGradientFragmentShader,
          buildUniforms(settingsRef.current, noiseTexture),
          { antialias: false, powerPreference: "low-power" },
          speedRef.current,
          frameRef.current,
          1,
          maxPixelCountRef.current
        );
      } catch (error) {
        // WebGL2 unavailable or the program failed to compile, and the host element's
        // background becomes the whole picture. Not reported in production: on a machine
        // without a GPU this is the normal path, not an incident, and the front door
        // would generate constant noise. In development it is worth saying, because a
        // shader-compile mistake looks exactly the same from the outside.
        if (process.env.NODE_ENV === "development") {
          console.warn("KuikaGrainGradient: shader unavailable, falling back to CSS.", error);
        }

        // The mount creates and prepends its canvas BEFORE it asks for the context, so a
        // throw leaves one behind — and `dispose()`, the only thing that removes it, is
        // now unreachable. It is invisible (the parent never received the
        // `data-paper-shader` attribute that styles it) but it is still a leaked node.
        element.querySelectorAll("canvas").forEach((canvas: HTMLCanvasElement): void => {
          canvas.remove();
        });

        shaderRef.current = null;
        return;
      }

      // Upstream registers no handler for this, which leaves a permanently blank canvas
      // once a context is lost. Hiding it uncovers the host background again.
      element.addEventListener("webglcontextlost", handleContextLost, true);
      setIsInitialized(true);
    };

    void start();

    return (): void => {
      cancelled = true;
      element?.removeEventListener("webglcontextlost", handleContextLost, true);
      element?.classList.remove(CONTEXT_LOST_CLASS);
      shaderRef.current?.dispose();
      shaderRef.current = null;
    };
  }, []);

  // `colors` is a new array on every render, so the uniform effect keys off a derived
  // string rather than the array identity.
  const uniformsKey: string = [colors.join("|"), colorBack, shape, softness, intensity, noise, scale].join("~");

  useEffect((): void => {
    const shader: ShaderMount | null = shaderRef.current;

    if (!shader) {
      return;
    }

    const noiseTexture: HTMLImageElement | undefined = getCachedNoiseTexture();
    shader.setUniforms(buildUniforms(settingsRef.current, isTextureReady(noiseTexture) ? noiseTexture : undefined));
  }, [uniformsKey, isInitialized]);

  useEffect((): void => {
    shaderRef.current?.setSpeed(effectiveSpeed);
  }, [effectiveSpeed, isInitialized]);

  useEffect((): void => {
    shaderRef.current?.setMaxPixelCount(maxPixelCount);
  }, [maxPixelCount, isInitialized]);

  // `frame` is deliberately NOT re-applied after mount. `setFrame` re-seeks the shader's
  // clock, so calling it again — including on the render that flips `isInitialized` —
  // rewinds an animation that is already running and shows up as a jump.

  return <div ref={mountRef} className={classNames("kuika-grain-gradient", className)} aria-hidden="true" />;
};

export default KuikaGrainGradient;
